import {
  AvoidTheWaitOrderOnline,
  Button,
  Flex,
  IconCartFill,
  IconCatering,
  IconDelivery,
  RapidRescue,
  ScrollView,
  YStack,
  YStackProps,
} from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import { Link } from '@fhs/link';
import { Image } from 'expo-image';
import { useCallback, useState } from 'react';
import { LayoutChangeEvent, Platform, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHomeHeader } from './screen-home-header';
import { region } from 'frontend/src/utils/environment';

function useOnLayoutHeightChange(
  currentHeight: number,
  onHeightChange: (nextHeight: number) => void
) {
  // storing these in boxes because we don't need to respond to their values changing
  // we just want to use the current values the instant the handler is invoked.
  const currentHeightRef = useBoxRef(currentHeight);
  const onHeightChangeRef = useBoxRef(onHeightChange);

  return useCallback((evt: LayoutChangeEvent) => {
    const nextHeight = evt.nativeEvent.layout.height;
    if (currentHeightRef.current !== nextHeight) {
      onHeightChangeRef.current(nextHeight);
    }
  }, []);
}

type CtaButtonStackProps = {
  onLayout?: YStackProps['onLayout'];
  style?: YStackProps['style'];
  isDeliveryEnabled: boolean;
  isCateringEnabled: boolean;
};

function CtaButtonStack(props: CtaButtonStackProps) {
  const safeAreaInsets = useSafeAreaInsets();
  // TODO: in version 8.0.0 this can be changed to use the .ca extension
  const cateringLink = region() === 'CA' ?  '/order-catering' : 'https://www.ezcater.com/brand/pvt/firehouse-subs';

  return (
    <YStack
      style={[
        styles.ctaButtonStack,
        { paddingBottom: Math.max(safeAreaInsets.bottom, 16) },
        props.style,
      ]}
      onLayout={props.onLayout}
    >
      <Link href="/menu">
        <Button type="solid" size="xl">
          <Button.Icon>
            <IconCartFill />
          </Button.Icon>
          <Button.Text>Pick Up Order</Button.Text>
        </Button>
      </Link>

      {props.isDeliveryEnabled && (
        <Link href="/store-locator/address">
          <Button type="outline" size="xl">
            <Button.Icon>
              <IconDelivery />
            </Button.Icon>
            <Button.Text>Delivery Order</Button.Text>
          </Button>
        </Link>
      )}

      <Link href={cateringLink}>
        <Button type="outline" size="xl" disabled={!props.isCateringEnabled}>
          <Button.Icon>
            <IconCatering />
          </Button.Icon>
          <Button.Text>Catering Order</Button.Text>
        </Button>
      </Link>
    </YStack>
  );
}

type ScreenHomeProps = {
  isSignedIn?: boolean;
  isDeliveryEnabled: boolean;
  isCateringEnabled: boolean;
};

export function ScreenHome(props: ScreenHomeProps) {
  const [heightAvailable, setHeightAvailable] = useState(0);
  const handleScrollViewLayoutChange = useOnLayoutHeightChange(heightAvailable, setHeightAvailable);

  const [nonCtaContentHeight, setNonCtaHeight] = useState(0);
  const handleNonCtaContentHeightLayoutChange = useOnLayoutHeightChange(
    nonCtaContentHeight,
    setNonCtaHeight
  );

  const [buttonStackHeight, setButtonStackHeight] = useState(0);
  const handleCtaButtonStackLayoutChange = useOnLayoutHeightChange(
    buttonStackHeight,
    setButtonStackHeight
  );

  return (
    <Flex style={styles.screen}>
      <ScreenHomeHeader isSignedIn={props.isSignedIn ?? false} />
      <ScrollView
        onLayout={handleScrollViewLayoutChange}
        contentContainerStyle={[styles.screenContent, { paddingBottom: buttonStackHeight }]}
      >
        <Image
          source={require('./brick-wall.jpg')}
          alt=""
          contentFit="cover"
          contentPosition="top"
          style={styles.brickWall}
          {...Platform.select({ web: { draggable: false } })}
        />

        <YStack style={styles.nonCtaContent} onLayout={handleNonCtaContentHeightLayoutChange}>
          <Image
            source={require('./marketing-ppms.webp')}
            alt="Pepperoni Pizza Meatball Sub on a garlic bread roll"
            contentFit="contain"
            style={styles.marketingImage}
          />
          <RapidRescue />
          <AvoidTheWaitOrderOnline />
        </YStack>
      </ScrollView>
      <CtaButtonStack
        isCateringEnabled={props.isCateringEnabled}
        isDeliveryEnabled={props.isDeliveryEnabled}
        onLayout={handleCtaButtonStackLayoutChange}
        style={
          heightAvailable < nonCtaContentHeight + buttonStackHeight && styles.floatingCtaButtonStack
        }
      />
    </Flex>
  );
}

const styles = StyleSheet.create({
  nonCtaContent: {
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'center',
    paddingTop: 48,
    paddingBottom: 32,
  },

  screen: {
    position: 'relative',
    backgroundColor: 'white',
  },

  screenContent: {
    justifyContent: 'flex-start',
  },

  brickWall: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.15,
  },

  marketingImage: {
    height: 348,
    width: '100%',

    paddingHorizontal: 16,
  },

  ctaButtonStack: {
    gap: 8,
    paddingHorizontal: 16,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: 16,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    backgroundColor: 'transparent',
  },

  floatingCtaButtonStack: {
    backgroundColor: 'white',
  },
});
